/**
 * A COACH'S PRIVATE NOTES ABOUT ONE CLIENT — the web half, **live since 2026-09-19.**
 *
 * 🔴 THE FLAG IS **ON** SINCE 2026-09-19 09:08, AND IT WAS TURNED ON BY THE TABLE GOING LIVE —
 * not by a decision to enable it. `coach_client_notes` exists on the live database (migration
 * `20260919092000`, applied by the session that heard HIS word: table, RLS and grants, six
 * rehearsal arms green and re-driven rolled back against the live table, 2 policies / 0 rows).
 * The client has no policy and no grant, which is what makes the privacy line below true.
 *
 * ⚠️ AND IT WAS FLIPPED IN ONE COMMIT WITH THE REGENERATED TYPES AND THE WIRING, which is what
 * the OFF state was protecting: a flag whose only writer is a future intention is the disguise
 * this repo deletes on sight, and a card wired against a table that is not there either errors
 * or — worse — appears to save.
 */

/**
 * 🔴 ON. The table is live; see above.
 *
 * ⚠️ A CONSTANT, NOT AN ENVIRONMENT READ. An `import.meta.env` flag is dead on a built bundle in
 * a way nobody can see, and this repo has paid for exactly that shape before — a branch whose
 * only hit was its own declaration. One constant, one reader, greppable.
 *
 * ⚠️ IT STAYS RATHER THAN BEING DELETED WITH THE CARD INLINED, and that is deliberate for one
 * release: it is the single place that says "this surface depends on a table that was added
 * yesterday", and it is what a session turns OFF in one line if the policy ever has to be
 * withdrawn. It goes when the table is no longer new.
 */
export const COACH_NOTES_ENABLED = true;

/** What the card holds. Kept minimal on purpose: a note is text and a time, nothing else. */
export type CoachNote = {
  /** `coach_clients.id` — the RELATIONSHIP, never the client. A note must not survive a
   *  relationship ending and re-starting, which is a different relationship. */
  linkId: string;
  body: string;
  updatedAtISO: string | null;
};

/**
 * Is there anything to show?
 *
 * ⚠️ AN EMPTY NOTE AND NO NOTE ARE THE SAME THING TO A READER and deliberately not distinguished:
 * a coach who cleared the box has no note, and a card that says "you wrote nothing here on
 * Tuesday" is noise. What IS distinguished, one layer up, is "not loaded yet" — which is
 * `undefined`, never an empty string.
 */
export function hasNote(note: CoachNote | undefined): boolean {
  return note !== undefined && note.body.trim() !== "";
}

/**
 * The line under the box. Null when there is nothing to say.
 *
 * ⚠️ IT NEVER CLAIMS A SAVE THAT DID NOT HAPPEN. `updatedAtISO` is the server's value, read back
 * — not a local timestamp set when the request was sent. A "saved just now" written by the client
 * is a sentence about an intention.
 */
export function noteFooter(note: CoachNote | undefined, now: Date): string | null {
  if (note === undefined || note.updatedAtISO === null) return null;
  const then = new Date(note.updatedAtISO);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days < 0) return "Edited";                 // clock skew — say nothing specific
  if (days === 0) return "Edited today";
  if (days === 1) return "Edited yesterday";
  if (days < 14) return `Edited ${days} days ago`;
  return `Edited ${Math.floor(days / 7)} weeks ago`;
}

/**
 * The sentence the card shows about WHO CAN SEE THIS.
 *
 * 🔴 IT IS NOT DECORATION AND IT IS NOT REMOVABLE. The entire value of a private note is the
 * coach's confidence that it is private — a coach who suspects the client might read it writes a
 * different note, or none. The policy is what makes it true; this is what makes it BELIEVED, and
 * the two have to ship together.
 *
 * 🔴 **AND IT NAMES THE ADMIN, BECAUSE THE POLICY DOES (W18(c), Fable's ruling 2026-09-19).**
 * The first spelling read *"Only you can see this. Your client cannot, and it is never part of
 * their program."* — true of a CLIENT and false of this database, which carries
 * `coach_client_notes_admin_read`: any identity `is_admin_identity` answers true for reads every
 * coach's notes. MEASURED 2026-09-19 (`~/ht-logs/day-0919/web-dig/notes/rehearse-card-write.sql`
 * V6): that is true for `3dddb14a-…`, who is the CLIENT of the only live coaching link, and
 * false for the other five profiles. Benign in fact — both accounts are his — and the table's own
 * `comment on` warned of it before the card was built.
 *
 * ⚠️ **THE REPAIR IS THE SENTENCE, NOT THE POLICY.** Narrowing `coach_client_notes_admin_read`
 * is the SECURITY SURFACE and stays his (`LAUNCH.md` §6); what was in this repo's gift was a
 * claim the database does not enforce, which is the same shape as the `AssignPanel` label found
 * the same morning. **Never a privacy claim the database does not enforce** — so the admin is
 * named, the client half (the half a coach actually cares about) is kept word for word, and the
 * sentence is now true of every row in this table.
 *
 * ⚠️ It says *"the app's admin"* rather than naming a person: the admin is an IDENTITY the SQL
 * decides (`is_admin_identity`), it can change without this string changing, and a coach reading
 * it needs to know the category exists rather than who is currently in it.
 */
export const NOTES_PRIVACY_LINE =
  "Private — only you and the app's admin can read this; your client cannot, "
  + "and it is never part of their program.";
